import React, { useState, useEffect } from 'react';
import '../App.css';
import RedirectFlow from './RedirectFlow';
import DecoupledFlow from './DecoupledFlow';
import GatewayFlow from './GatewayFlow';

const Loan: React.FC = () => {
    const [applicationNumber, setApplicationNumber] = useState<string>('');
    const [showRedirectFlow, setShowRedirectFlow] = useState<boolean>(false);
    const [showDecoupledFlow, setShowDecoupledFlow] = useState<boolean>(false);
    const [showGatewayFlow, setShowGatewayFlow] = useState<boolean>(false);
    const [selectedFlow, setSelectedFlow] = useState<string>('redirect');
    const [currentDate] = useState<string>(new Date().toLocaleDateString('en-NZ', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
    }));

    // Random data for demo purposes
    const addresses = [
        '123 Queen Street, Auckland CBD, Auckland 1010',
        '456 Lambton Quay, Wellington Central, Wellington 6011',
        '789 George Street, Dunedin Central, Dunedin 9016',
        '321 Victoria Street, Hamilton Central, Hamilton 3204',
        '654 Colombo Street, Christchurch Central, Christchurch 8011',
        '987 Devon Street, New Plymouth Central, New Plymouth 4310',
        '147 Emerson Street, Napier South, Napier 4110',
        '258 Palmerston Road, Gisborne, Gisborne 4010',
        '369 The Square, Palmerston North Central, Palmerston North 4410',
        '741 Grey Street, Hamilton East, Hamilton 3216'
    ];

    const emails = [
        'john.wick@continental.com',
        'j.wick@babel.org',
        'johnwick@marker.net',
        'jwick@highttable.com',
        'john.w@excommunicado.org',
        'jardani@roma.it',
        'j.wick.nyc@gmail.com',
        'baba.yaga@underworld.net',
        'john@thecontinental.org',
        'wick.john@assassin.pro'
    ];

    const phoneNumbers = [
        '+64 21 123 4567',
        '+64 22 234 5678',
        '+64 27 345 6789',
        '+64 21 456 7890',
        '+64 22 567 8901',
        '+64 27 678 9012',
        '+64 21 789 0123',
        '+64 22 890 1234',
        '+64 27 901 2345',
        '+64 21 012 3456'
    ];

    const [randomAddress] = useState<string>(addresses[Math.floor(Math.random() * addresses.length)]);
    const [randomEmail] = useState<string>(emails[Math.floor(Math.random() * emails.length)]);
    const [randomPhone] = useState<string>(phoneNumbers[Math.floor(Math.random() * phoneNumbers.length)]);

    useEffect(() => {
        // Check if returning from redirect with authorization
        const urlParams = new URLSearchParams(window.location.search);
        const consentIdParam = urlParams.get('consent_id');
        const code = urlParams.get('code');
        const state = urlParams.get('state');

        if (consentIdParam || code || state) {
            // Redirect to the dedicated redirect page with the consent info
            const params = new URLSearchParams();
            if (consentIdParam) params.set('cid', consentIdParam);
            if (code) params.set('code', code);
            if (state) params.set('state', state);

            window.location.href = `/redirect?${params.toString()}`;
            return;
        }

        // Generate random 6-digit application number
        const generateAppNumber = () => {
            const num = Math.floor(100000 + Math.random() * 900000).toString();
            setApplicationNumber(num);
        };

        generateAppNumber();
    }, []);

    const loanAmount = 1000000;
    const interestRate = 0.20;
    const totalAmount = loanAmount * (1 + interestRate);

    const formatCurrency = (amount: number): string => {
        return new Intl.NumberFormat('en-NZ', {
            style: 'currency',
            currency: 'NZD',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(amount);
    };

    const handleRedirectFlowSubmit = (payload: any) => {
        console.log('Redirect Flow Initiated with payload:', payload);
        // Here you would typically make an API call to initiate the redirect flow
        setShowRedirectFlow(false);
    };

    const handleDecoupledFlowSubmit = (payload: any) => {
        console.log('Decoupled Flow Initiated with payload:', payload);
        // Here you would typically make an API call to initiate the decoupled flow
        setShowDecoupledFlow(false);
    };

    const handleGatewayFlowSubmit = (payload: any) => {
        console.log('Gateway Flow Initiated with payload:', payload);
        // Here you would typically make an API call to initiate the gateway flow
        setShowGatewayFlow(false);
    };

    return (
        <div className="loan-container">
            <div className="loan-form">
                <h2 className="loan-title">Loan Application</h2>

                <div className="loan-header">
                    <div className="header-item">
                        <label>Application Date:</label>
                        <span className="value">{currentDate}</span>
                    </div>
                    <div className="header-item">
                        <label>Application Number:</label>
                        <span className="value app-number">#{applicationNumber}</span>
                    </div>
                </div>

                <div className="form-section">
                    <h3>Personal Information</h3>
                    <div className="form-grid">
                        <div className="form-group">
                            <label>First Name:</label>
                            <input type="text" value="John" readOnly className="form-input" />
                        </div>
                        <div className="form-group">
                            <label>Last Name:</label>
                            <input type="text" value="Wick" readOnly className="form-input" />
                        </div>
                        <div className="form-group full-width">
                            <label>Home Address:</label>
                            <input type="text" value={randomAddress} readOnly className="form-input" />
                        </div>
                        <div className="form-group">
                            <label>Email Address:</label>
                            <input type="email" value={randomEmail} readOnly className="form-input" />
                        </div>
                        <div className="form-group">
                            <label>Phone Number:</label>
                            <input type="tel" value={randomPhone} readOnly className="form-input" />
                        </div>
                    </div>
                </div>

                <div className="form-section">
                    <h3>Loan Details</h3>
                    <div className="loan-details">
                        <div className="detail-row">
                            <label>Loan Amount:</label>
                            <span className="amount">{formatCurrency(loanAmount)}</span>
                        </div>
                        <div className="detail-row">
                            <label>Interest Rate:</label>
                            <span className="rate">20%</span>
                        </div>
                        <div className="detail-row total">
                            <label>Total Amount:</label>
                            <span className="total-amount">{formatCurrency(totalAmount)}</span>
                        </div>
                    </div>
                </div>

                <div className="credit-check-section">
                    <h3>Credit Check Configuration</h3>

                    <div className="flow-selector">
                        <label className="flow-selector-label">Select Authentication Flow:</label>
                        <div className="flow-options">
                            <label className={`flow-option ${selectedFlow === 'redirect' ? 'selected' : ''}`}>
                                <input
                                    type="radio"
                                    name="flow"
                                    value="redirect"
                                    checked={selectedFlow === 'redirect'}
                                    onChange={(e) => setSelectedFlow(e.target.value)}
                                />
                                <div className="flow-option-content">
                                    <span className="flow-name">Redirect Flow</span>
                                    <span className="flow-desc">Bank-specific redirect authentication</span>
                                </div>
                            </label>
                            <label className={`flow-option ${selectedFlow === 'decoupled' ? 'selected' : ''}`}>
                                <input
                                    type="radio"
                                    name="flow"
                                    value="decoupled"
                                    checked={selectedFlow === 'decoupled'}
                                    onChange={(e) => setSelectedFlow(e.target.value)}
                                />
                                <div className="flow-option-content">
                                    <span className="flow-name">Decoupled Flow</span>
                                    <span className="flow-desc">Separate device authentication</span>
                                </div>
                            </label>
                            <label className={`flow-option ${selectedFlow === 'gateway' ? 'selected' : ''}`}>
                                <input
                                    type="radio"
                                    name="flow"
                                    value="gateway"
                                    checked={selectedFlow === 'gateway'}
                                    onChange={(e) => setSelectedFlow(e.target.value)}
                                />
                                <div className="flow-option-content">
                                    <span className="flow-name">Gateway Flow</span>
                                    <span className="flow-desc">Unified bank selection interface</span>
                                </div>
                            </label>
                        </div>
                    </div>

                    <div className="configure-button-container">
                        {selectedFlow === 'redirect' && (
                            <button
                                className="configure-btn redirect"
                                onClick={() => setShowRedirectFlow(true)}
                            >
                                Configure Redirect Flow
                            </button>
                        )}
                        {selectedFlow === 'decoupled' && (
                            <button
                                className="configure-btn decoupled"
                                onClick={() => setShowDecoupledFlow(true)}
                            >
                                Configure Decoupled Flow
                            </button>
                        )}
                        {selectedFlow === 'gateway' && (
                            <button
                                className="configure-btn gateway"
                                onClick={() => setShowGatewayFlow(true)}
                            >
                                Configure Gateway Flow
                            </button>
                        )}
                    </div>
                </div>
            </div>

            <style>{`
                .loan-container {
                    min-height: 100vh;
                    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    padding: 20px;
                    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif;
                }

                .loan-form {
                    background: white;
                    border-radius: 20px;
                    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
                    max-width: 700px;
                    width: 100%;
                    padding: 40px;
                }

                .loan-title {
                    color: #333;
                    font-size: 32px;
                    margin-bottom: 30px;
                    text-align: center;
                    font-weight: 700;
                    text-transform: uppercase;
                    letter-spacing: 1px;
                }

                .loan-header {
                    display: flex;
                    justify-content: space-between;
                    margin-bottom: 30px;
                    padding: 20px;
                    background: #f8f9fa;
                    border-radius: 10px;
                }

                .header-item {
                    display: flex;
                    flex-direction: column;
                }

                .header-item label {
                    font-size: 12px;
                    color: #666;
                    margin-bottom: 5px;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                }

                .header-item .value {
                    font-size: 16px;
                    color: #333;
                    font-weight: 600;
                }

                .app-number {
                    color: #667eea;
                    font-family: 'Courier New', monospace;
                    animation: pulse 5s infinite;
                }

                @keyframes pulse {
                    0%, 90% { opacity: 1; }
                    95% { opacity: 0.5; }
                    100% { opacity: 1; }
                }

                .form-section {
                    margin-bottom: 30px;
                }

                .form-section h3 {
                    color: #333;
                    font-size: 20px;
                    margin-bottom: 20px;
                    padding-bottom: 10px;
                    border-bottom: 2px solid #667eea;
                }

                .form-grid {
                    display: grid;
                    grid-template-columns: 1fr 1fr;
                    gap: 20px;
                }

                .form-group {
                    display: flex;
                    flex-direction: column;
                }

                .form-group.full-width {
                    grid-column: 1 / -1;
                }

                .form-group label {
                    font-size: 14px;
                    color: #555;
                    margin-bottom: 8px;
                    font-weight: 500;
                }

                .form-input {
                    padding: 12px;
                    border: 1px solid #ddd;
                    border-radius: 8px;
                    font-size: 16px;
                    background: #f8f9fa;
                    color: #333;
                    font-family: inherit;
                }

                .form-input:focus {
                    outline: none;
                    border-color: #667eea;
                    background: white;
                }

                .loan-details {
                    background: #f8f9fa;
                    border-radius: 10px;
                    padding: 20px;
                }

                .detail-row {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 15px 0;
                    border-bottom: 1px solid #e0e0e0;
                }

                .detail-row:last-child {
                    border-bottom: none;
                }

                .detail-row label {
                    font-size: 16px;
                    color: #555;
                    font-weight: 500;
                }

                .detail-row span {
                    font-size: 18px;
                    font-weight: 600;
                    color: #333;
                }

                .detail-row .amount {
                    color: #667eea;
                    font-size: 22px;
                }

                .detail-row .rate {
                    color: #f39c12;
                    font-size: 20px;
                }

                .detail-row.total {
                    background: #667eea;
                    color: white;
                    margin: -20px;
                    margin-top: 20px;
                    padding: 20px;
                    border-radius: 0 0 10px 10px;
                    border: none;
                }

                .detail-row.total label {
                    color: white;
                    font-size: 18px;
                }

                .detail-row.total .total-amount {
                    color: white;
                    font-size: 28px;
                    font-weight: 700;
                }

                .credit-check-section {
                    margin-top: 30px;
                }

                .credit-check-section h3 {
                    color: #333;
                    font-size: 20px;
                    margin-bottom: 20px;
                    padding-bottom: 10px;
                    border-bottom: 2px solid #667eea;
                }

                .flow-selector {
                    margin-bottom: 30px;
                }

                .flow-selector-label {
                    display: block;
                    color: #555;
                    font-size: 16px;
                    font-weight: 500;
                    margin-bottom: 15px;
                }

                .flow-options {
                    display: grid;
                    grid-template-columns: repeat(3, 1fr);
                    gap: 15px;
                }

                .flow-option {
                    display: flex;
                    align-items: flex-start;
                    padding: 20px;
                    border: 2px solid #e0e0e0;
                    border-radius: 12px;
                    cursor: pointer;
                    transition: all 0.3s ease;
                    background: white;
                }

                .flow-option:hover {
                    border-color: #667eea;
                    background: #f8f9fa;
                }

                .flow-option.selected {
                    border-color: #667eea;
                    background: linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%);
                }

                .flow-option input[type="radio"] {
                    margin-right: 12px;
                    margin-top: 2px;
                    accent-color: #667eea;
                }

                .flow-option-content {
                    display: flex;
                    flex-direction: column;
                    flex: 1;
                }

                .flow-name {
                    color: #333;
                    font-size: 16px;
                    font-weight: 600;
                    margin-bottom: 5px;
                }

                .flow-desc {
                    color: #777;
                    font-size: 13px;
                    line-height: 1.4;
                }

                .configure-button-container {
                    display: flex;
                    justify-content: center;
                }

                .configure-btn {
                    padding: 18px 40px;
                    font-size: 18px;
                    font-weight: 600;
                    border: none;
                    border-radius: 12px;
                    cursor: pointer;
                    transition: all 0.3s ease;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                    color: white;
                }

                .configure-btn.redirect {
                    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                }

                .configure-btn.redirect:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 8px 25px rgba(102, 126, 234, 0.4);
                }

                .configure-btn.decoupled {
                    background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
                }

                .configure-btn.decoupled:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 8px 25px rgba(240, 147, 251, 0.4);
                }

                .configure-btn.gateway {
                    background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
                }

                .configure-btn.gateway:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 8px 25px rgba(79, 172, 254, 0.4);
                }

                .configure-btn:active {
                    transform: translateY(0);
                }

                /* Authorized View Styles */
                .authorized-container {
                    background: white;
                    border-radius: 20px;
                    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
                    max-width: 1200px;
                    width: 100%;
                    overflow: hidden;
                }

                .authorized-header {
                    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                    color: white;
                    padding: 30px;
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                }

                .authorized-header h2 {
                    margin: 0;
                    font-size: 28px;
                }

                .new-application-btn {
                    padding: 12px 24px;
                    background: white;
                    color: #667eea;
                    border: none;
                    border-radius: 10px;
                    font-size: 16px;
                    font-weight: 600;
                    cursor: pointer;
                    transition: all 0.3s ease;
                }

                .new-application-btn:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 5px 20px rgba(255, 255, 255, 0.3);
                }

                .tabs-container {
                    min-height: 500px;
                }

                .tabs-nav {
                    display: flex;
                    border-bottom: 2px solid #e0e0e0;
                    background: #f8f9fa;
                    padding: 0 30px;
                }

                .tab-btn {
                    padding: 20px 30px;
                    background: none;
                    border: none;
                    color: #666;
                    font-size: 16px;
                    font-weight: 500;
                    cursor: pointer;
                    transition: all 0.3s ease;
                    border-bottom: 3px solid transparent;
                    margin-bottom: -2px;
                }

                .tab-btn:hover {
                    color: #667eea;
                    background: rgba(102, 126, 234, 0.05);
                }

                .tab-btn.active {
                    color: #667eea;
                    border-bottom-color: #667eea;
                    background: white;
                }

                .tab-btn.disabled {
                    color: #ccc;
                    cursor: not-allowed;
                    opacity: 0.5;
                }

                .tab-btn.disabled:hover {
                    color: #ccc;
                    background: transparent;
                }

                .tab-panel {
                    padding: 30px;
                }

                .tab-content h3 {
                    color: #333;
                    margin-bottom: 25px;
                    padding-bottom: 15px;
                    border-bottom: 2px solid #667eea;
                }

                /* Party Tab Styles */
                .info-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
                    gap: 20px;
                }

                .info-item {
                    display: flex;
                    flex-direction: column;
                    padding: 15px;
                    background: #f8f9fa;
                    border-radius: 10px;
                }

                .info-item label {
                    color: #666;
                    font-size: 14px;
                    margin-bottom: 8px;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                }

                .info-item span {
                    color: #333;
                    font-size: 18px;
                    font-weight: 500;
                }

                /* Consent Info Styles */
                .consent-info {
                    background: #f0f4ff;
                    padding: 10px 20px;
                    border-radius: 8px;
                    margin-bottom: 20px;
                    display: flex;
                    gap: 30px;
                    font-size: 14px;
                }

                .consent-info span {
                    color: #667eea;
                    font-weight: 500;
                }

                /* Accounts Tab Styles */
                .account-selection-hint {
                    color: #666;
                    margin-bottom: 20px;
                }

                .accounts-radio-list {
                    display: flex;
                    flex-direction: column;
                    gap: 15px;
                }

                .account-radio-card {
                    display: flex;
                    align-items: center;
                    padding: 20px;
                    border: 2px solid #e0e0e0;
                    border-radius: 12px;
                    cursor: pointer;
                    transition: all 0.3s ease;
                    background: white;
                }

                .account-radio-card:hover {
                    border-color: #667eea;
                    background: #f8f9fa;
                    transform: translateX(5px);
                }

                .account-radio-card.selected {
                    border-color: #667eea;
                    background: linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%);
                    box-shadow: 0 4px 15px rgba(102, 126, 234, 0.2);
                }

                .account-radio-card input[type="radio"] {
                    margin-right: 20px;
                    width: 20px;
                    height: 20px;
                    accent-color: #667eea;
                }

                .account-radio-content {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    width: 100%;
                }

                .account-main h4 {
                    margin: 0 0 8px 0;
                    color: #333;
                    font-size: 18px;
                }

                .account-main .account-number {
                    color: #666;
                    font-family: monospace;
                    font-size: 14px;
                    margin: 0;
                }

                .account-details {
                    text-align: right;
                }

                .account-details .account-balance {
                    color: #667eea;
                    font-size: 24px;
                    font-weight: 700;
                    margin: 0 0 8px 0;
                }

                .account-details .account-type {
                    display: inline-block;
                    padding: 4px 12px;
                    background: #667eea;
                    color: white;
                    border-radius: 20px;
                    font-size: 12px;
                    text-transform: uppercase;
                }

                .account-selected-message {
                    margin-top: 20px;
                    padding: 15px;
                    background: #d4edda;
                    border-radius: 8px;
                    color: #155724;
                    font-weight: 500;
                }

                .no-account-selected {
                    padding: 40px;
                    text-align: center;
                    background: #f8f9fa;
                    border-radius: 10px;
                    color: #666;
                }

                .loading-message {
                    padding: 20px;
                    text-align: center;
                    color: #667eea;
                    font-style: italic;
                    animation: pulse 1.5s ease-in-out infinite;
                }

                .error-message {
                    padding: 15px;
                    background: #f8d7da;
                    border: 1px solid #f5c6cb;
                    border-radius: 8px;
                    color: #721c24;
                    margin-bottom: 20px;
                }

                .account-id {
                    display: block;
                    color: #999;
                    font-size: 12px;
                    margin-top: 5px;
                }

                .accounts-list {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
                    gap: 20px;
                }

                .account-card {
                    padding: 25px;
                    background: #f8f9fa;
                    border-radius: 15px;
                    border: 2px solid #e0e0e0;
                    transition: all 0.3s ease;
                }

                .account-card:hover {
                    border-color: #667eea;
                    transform: translateY(-5px);
                    box-shadow: 0 10px 30px rgba(102, 126, 234, 0.2);
                }

                .account-card h4 {
                    margin: 0 0 15px 0;
                    color: #333;
                    font-size: 20px;
                }

                .account-number {
                    color: #666;
                    font-family: monospace;
                    margin: 10px 0;
                    font-size: 14px;
                }

                .account-balance {
                    color: #667eea;
                    font-size: 28px;
                    font-weight: 700;
                    margin: 15px 0;
                }

                .account-type {
                    display: inline-block;
                    padding: 5px 12px;
                    background: #667eea;
                    color: white;
                    border-radius: 20px;
                    font-size: 12px;
                    text-transform: uppercase;
                }

                /* Balances Tab Styles */
                .balances-summary {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
                    gap: 20px;
                    margin-bottom: 30px;
                }

                .balance-item {
                    padding: 20px;
                    background: #f8f9fa;
                    border-radius: 10px;
                    display: flex;
                    flex-direction: column;
                }

                .balance-item label {
                    color: #666;
                    font-size: 14px;
                    margin-bottom: 10px;
                }

                .balance-amount {
                    color: #333;
                    font-size: 24px;
                    font-weight: 600;
                }

                .balance-amount.total {
                    color: #667eea;
                    font-size: 32px;
                }

                .balance-chart {
                    padding: 25px;
                    background: #f8f9fa;
                    border-radius: 10px;
                    text-align: center;
                }

                /* Statements Tab Styles */
                .statements-list {
                    display: flex;
                    flex-direction: column;
                    gap: 15px;
                }

                .statement-item {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 20px;
                    background: #f8f9fa;
                    border-radius: 10px;
                    transition: all 0.3s ease;
                }

                .statement-item:hover {
                    background: #e8eaed;
                }

                .statement-date {
                    font-size: 16px;
                    font-weight: 600;
                    color: #333;
                }

                .statement-account {
                    color: #666;
                }

                .download-btn {
                    padding: 8px 20px;
                    background: #667eea;
                    color: white;
                    border: none;
                    border-radius: 8px;
                    font-size: 14px;
                    font-weight: 500;
                    cursor: pointer;
                    transition: all 0.3s ease;
                }

                .download-btn:hover {
                    background: #5568d3;
                    transform: translateY(-2px);
                }

                /* Transactions Tab Styles */
                .transactions-list {
                    display: flex;
                    flex-direction: column;
                    gap: 15px;
                }

                .transaction-item {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 20px;
                    background: #f8f9fa;
                    border-radius: 10px;
                    border-left: 4px solid transparent;
                    transition: all 0.3s ease;
                }

                .transaction-item.credit {
                    border-left-color: #4caf50;
                }

                .transaction-item.debit {
                    border-left-color: #f44336;
                }

                .transaction-item:hover {
                    background: #e8eaed;
                    transform: translateX(5px);
                }

                .transaction-info {
                    display: flex;
                    flex-direction: column;
                    gap: 5px;
                }

                .transaction-date {
                    color: #666;
                    font-size: 14px;
                }

                .transaction-desc {
                    color: #333;
                    font-size: 16px;
                    font-weight: 500;
                }

                .transaction-amount {
                    font-size: 18px;
                    font-weight: 600;
                }

                .transaction-item.credit .transaction-amount {
                    color: #4caf50;
                }

                .transaction-item.debit .transaction-amount {
                    color: #f44336;
                }

                @media (max-width: 600px) {
                    .loan-form {
                        padding: 20px;
                    }

                    .loan-title {
                        font-size: 24px;
                    }

                    .loan-header {
                        flex-direction: column;
                        gap: 15px;
                    }

                    .form-grid {
                        grid-template-columns: 1fr;
                    }

                    .detail-row.total .total-amount {
                        font-size: 22px;
                    }

                    .flow-options {
                        grid-template-columns: 1fr;
                    }

                    .flow-option {
                        padding: 15px;
                    }

                    .configure-btn {
                        font-size: 16px;
                        padding: 15px 30px;
                        width: 100%;
                    }

                    /* Authorized View Mobile Styles */
                    .authorized-header {
                        flex-direction: column;
                        gap: 15px;
                        text-align: center;
                    }

                    .authorized-header h2 {
                        font-size: 20px;
                    }

                    .tabs-nav {
                        overflow-x: auto;
                        padding: 0;
                    }

                    .tab-btn {
                        padding: 15px 20px;
                        font-size: 14px;
                        white-space: nowrap;
                    }

                    .info-grid,
                    .accounts-list {
                        grid-template-columns: 1fr;
                    }

                    .statement-item {
                        flex-direction: column;
                        gap: 10px;
                        text-align: center;
                    }

                    .transaction-item {
                        padding: 15px;
                    }

                    .transaction-amount {
                        font-size: 16px;
                    }
                }
            `}</style>

            {showRedirectFlow && (
                <RedirectFlow
                    onSubmit={handleRedirectFlowSubmit}
                    onClose={() => setShowRedirectFlow(false)}
                />
            )}

            {showDecoupledFlow && (
                <DecoupledFlow
                    onSubmit={handleDecoupledFlowSubmit}
                    onClose={() => setShowDecoupledFlow(false)}
                />
            )}

            {showGatewayFlow && (
                <GatewayFlow
                    onSubmit={handleGatewayFlowSubmit}
                    onClose={() => setShowGatewayFlow(false)}
                />
            )}
        </div>
    );
};

export default Loan;